<template>
  <div>
      <card title="Refill Request Batches">
        <b-row>
            <b-col cols="6">
                <fg-input label="Start Date" required>
                    <el-date-picker v-model="startDate"
                                    type="date"></el-date-picker>
                </fg-input>
            </b-col>
            <b-col cols="6">
                <fg-input label="End Date" required>
                    <el-date-picker v-model="endDate"
                                    type="date"></el-date-picker>
                </fg-input>
            </b-col>
        </b-row>
        <div v-if="isSaving">
            <b-spinner label="Spinning" class="mr-2"></b-spinner>
            <strong>Saving...</strong>
        </div>
        <b-row v-else>
            <b-col>
                <b-button @click="loadData" variant="primary">
                    Load Scripts
                </b-button>
                <b-button @click="confirm" variant="success" v-if="!disableCreate">
                    Create Batch
                </b-button>
                <b-button @click="showHistory" variant="info">Show History</b-button>
            </b-col>
        </b-row>
        <PrescriptionsListComponent
            ref="prescriptionList"
            :prescriptions="pagedPrescriptions"
            :showGeneralActionButtons="false"
            :showSelectCheckox="false"
            :showVoidBox="false"
            :actionButtons="['edit','delete']"
            :isLoading="isLoading"
            :manual-sort="['productNameShort','daySupply', 'dispensed', 'fillDate']"
            :default-sort="sortBy"
            @sort-changed="handleSort"
            @prescriptionRemoved="rxRemoved"
        ></PrescriptionsListComponent>

        <BottomPagerBar
            v-model="pageNumber"
            :total-rows="sortedPrescriptions.length"
            :per-page="pageSize"
            show-total
            item-name="prescription"
            :show-page-size-selector="true"
            :loading="isLoading"
            @change-page-size="pageSize = $event"
        />

      </card>

    <!-- History -->
    <b-modal id="historyModal" ref="historyModal"
                hide-footer
                size="xl" title="Refill Request Batch History">
        <RefillRequestBatchHistory></RefillRequestBatchHistory>
    </b-modal>
  </div>
</template>

<script lang='ts'>
import { Prescription } from '@/models/Prescription'
import {Component, Vue, Watch } from 'vue-property-decorator'
import {RefillRequestBatch} from '@/models/RefillRequestBatch';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import RefillRequestBatchHistory from '@/components/RefillRequestBatch/RefillRequestBatchHistory.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { DatePicker } from 'element-ui'
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';

@Component({
    name: 'RefillRequestBatchPage',
    components: {
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        RefillRequestBatchHistory,
        BottomPagerBar
    }
})
export default class RefillRequestBatchPage extends Vue {
    private items:Array<Prescription> = new Array<Prescription>();
    private startDate : Date | null = null;
    private endDate : Date | null= null;

    private pageNumber: number = 1;
    private pageSize: number = 100;
    private sortBy: string = "fillDate";
    private sortDirection: string = "D"; // D = Descending, A = Ascending

    get disableCreate():boolean {
        return !this.items?.length;
    }

    // Computed getter for sorted prescriptions
    get sortedPrescriptions(): Array<Prescription> {
        let sorted = [...this.items]; // Copy to avoid mutation

        switch (this.sortBy) {
            case "productNameShort":
                sorted.sort((a, b) => (a.productNameShort || "").localeCompare(b.productNameShort || ""));
                break;
            case "programName":
                sorted.sort((a, b) => (a.programName || "").trim().localeCompare((b.programName || "").trim()));
                break;
            case "daySupply":
                sorted.sort((a, b) => (a.daySupply || 0) - (b.daySupply || 0));
                break;
            case "dispensed":
                sorted.sort((a, b) => (a.dispensed || 0) - (b.dispensed || 0));
                break;
            default: // fillDate
                sorted.sort((a, b) => new Date(a.fillDate).getTime() - new Date(b.fillDate).getTime());
                break;
        }

        if (this.sortDirection === "D") sorted.reverse();

        return sorted;
    }

    get pagedPrescriptions(): Array<Prescription> {
        const startIndex = this.pageSize * this.pageNumber - this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.sortedPrescriptions.slice(startIndex, endIndex);
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDirection = event.sortDesc ? "D" : "A";
        this.pageNumber = 1;
    }

    created(){
        this.pageSize = getPageSizePreference(this, 'pageSize_RefillRequestBatch', 100);
        this.endDate = moment().toDate();
        this.startDate = moment().subtract(1, 'month').toDate();
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, 'pageSize_RefillRequestBatch', this.pageSize);
    }

    private isLoading = false;
    loadData() {
        if(this.startDate === null || this.endDate === null || moment(this.startDate).isAfter(this.endDate)) return;

        this.isLoading = true;
        this.pageNumber = 1; // Reset to first page when loading new data
        this.$http.get<Array<Prescription>>('RefillRequestBatch/eligible-for-refill-request', {params: {
            startDate: this.startDate, endDate: this.endDate,
        }})
        .then(res => {
            this.items = res.data?.map(rx => new Prescription(rx)) || new Array<Prescription>();
        })
        .catch(err => {
            console.error('Error loading eligible rxs for refill request', {err, response: err.response});
            this.$notification(NotificationOptions.error(err));
        })
        .finally(() => {
            this.isLoading = false;
        })
    }

    confirm(){
        this.$bvModal.msgBoxConfirm('Are you sure you want to request a refill for all items in the list?', {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
        })
        .then(value => {
            if (!value) return;
            this.createBatch();
        })
        .catch(err => {
            console.error("Error caught on Clear button.", err);
        });
    }

    private isSaving = false;
    createBatch(){
        this.isSaving = true;
        this.$http.post<RefillRequestBatch>('RefillRequestBatch', this.items)
        .then(res => {
            if(res.data?.id) {
                this.loadData();
                this.$notification(NotificationOptions.notificationOptionsPreset("Refill Request is being executed in batch for all items in the list.", NotificationOptions.NotificationTypes.success))
            }
        })
        .catch(err => {
            console.error('Error creating new refill request batch.', {err, response: err.response});
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Refill Request Batch.", err));
        })
        .finally(() => {
            this.isSaving = false;
        })
    }

    rxRemoved(item:Prescription){
        this.items = this.items.filter(i=> i.rxID != item.rxID);
    }

    showHistory(){
        this.$bvModal.show('historyModal');
    }
}
</script>

<style>

</style>
